/**
 * Struggles Page - Fourth and final onboarding page (Step 4 of 4).
 * Feature 016: Conversational onboarding flow for identifying wellness struggles.
 *
 * User Story 5: "Your Biggest Struggles 💬"
 * Collects: struggles (optional multi-select)
 * Marks onboarding complete and redirects to home page.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import ProgressIndicator from '../../components/onboarding/ProgressIndicator';

// T065: Struggle options with emoticons
const STRUGGLE_OPTIONS = [
  { value: 'weight_wont_go_down', label: 'My weight won\'t go down', emoji: '⚖️' },
  { value: 'belly_fat_hard_to_lose', label: 'Belly fat is hard to lose', emoji: '🎯' },
  { value: 'feel_tired', label: 'I feel tired all the time', emoji: '😴' },
  { value: 'cant_stay_consistent', label: 'I cannot stay consistent', emoji: '📅' },
  { value: 'body_feels_unpredictable', label: 'My body feels different / unpredictable', emoji: '🌀' },
  { value: 'feel_bloated', label: 'I feel bloated or heavy often', emoji: '💧' }
];

const StrugglesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { savePage, isSaving } = useOnboarding();

  // T067: Checkbox state array
  const [selectedStruggles, setSelectedStruggles] = useState<string[]>([]);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Pre-fill existing data if user returns to this page
  useEffect(() => {
    if (user?.profile?.struggles) {
      setSelectedStruggles(user.profile.struggles);
    }
  }, [user]);

  // T067: Toggle struggle selection
  const handleStruggleToggle = (value: string) => {
    setSelectedStruggles((prev) =>
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value]
    );
  };

  // T069: Handle form submission
  const handleNext = async () => {
    // Prepare data for API (struggles is optional, can be empty array)
    const pageData = {
      struggles: selectedStruggles
    };

    try {
      // Save struggles via API - backend will mark onboarding complete
      await savePage.mutateAsync({ page: 'struggles', data: pageData });

      // T070: Success toast with celebration
      setToastType('success');
      setToastMessage('Welcome to BOSI! 🎉');
      setShowToast(true);

      // T071: Navigate to welcome page (redirect happens after toast)
      setTimeout(() => {
        navigate('/welcome');
      }, 1500);
    } catch (err: any) {
      // Error handling
      setToastType('error');
      setToastMessage(err.message || 'Failed to save struggles. Please try again.');
      setShowToast(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4 py-12">
      <div className="max-w-2xl w-full animate-fade-in">
        {/* T064: Progress Indicator - Step 4 of 4, 100% */}
        <ProgressIndicator currentStep={4} totalSteps={4} />

        <div className="card shadow-elegant mt-8">
          {/* T062-T063: Page header with supportive messaging */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-serif font-bold text-primary-800 mb-3">
              Your Biggest Struggles 💬
            </h1>
            <p className="text-base text-neutral-700 font-medium mb-1">
              What frustrates you most right now?
            </p>
            <p className="text-base text-neutral-600 italic">
              You're not alone. We'll walk you through this together.
            </p>
          </div>

          {/* Toast notification with aria-live for screen readers (T086) */}
          {showToast && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={`mb-6 p-4 border-l-4 rounded-r-lg animate-slide-down ${
                toastType === 'success'
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  toastType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {toastMessage}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* T065-T066: Checkbox options with emoticons */}
            {STRUGGLE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-center p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                  selectedStruggles.includes(option.value)
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-primary-200 bg-white hover:border-primary-400'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedStruggles.includes(option.value)}
                  onChange={() => handleStruggleToggle(option.value)}
                  className="w-5 h-5 text-primary-600 rounded mr-4 focus:ring-primary-500"
                />
                <span className="text-2xl mr-3">{option.emoji}</span>
                <span className="text-sm font-medium text-neutral-800">
                  {option.label}
                </span>
              </label>
            ))}

            {/* T068: Complete Onboarding button (not "Next →") */}
            <button
              type="button"
              onClick={handleNext}
              disabled={isSaving}
              className="btn btn-primary w-full text-lg py-4 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Complete Onboarding'}
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-6 text-xs text-center text-neutral-600">
          We'll use this to personalize your daily guidance and support
        </p>
      </div>
    </div>
  );
};

export default StrugglesPage;
